import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { DialogService } from '@ms/core';
import { KeyValueDTO } from '@ms/core/model/keyvalue.model';
import { EventManager } from '@ms/core/service/event-manager.service';
import { BudgetImportDTO, BudgetPermission, IBudget, IBudgetSearchResultDTO } from 'app/main/budgets/budgets.model';
import { AccountCategory } from 'app/main/shared/enum/budget.enum';
import { Observable, Subscription } from 'rxjs';
import { Budget, BudgetSearch } from '../budgets/budgets.model';
import { BudgetCopy } from '../shared/model/budget-copy.model';
import { BudgetConstant } from '../shared/model/budget.model';

@Injectable({
    providedIn: 'root',
})
export class BudgetService implements OnDestroy {
    baseUrl = '/school-service/api/budgets';
    importDataSubscriber: Subscription;
    constructor(
        private http: HttpClient,
        private eventManager: EventManager,
        private dialogService: DialogService
    ) {
        console.log('Budget service created');
        this.importDataSubscriber = this.eventManager.subscribe('data-import-finished', content => {
            console.log('Message received', content);
            if (content.message?.type == 'ERROR') {
                this.dialogService.error(content.message.message);
            } else {
                this.dialogService.info(content.message.message);
            }
        });
    }

    getBudgetById(id: string): Observable<Budget> {
        return this.http.get<Budget>(`${this.baseUrl}/${id}`);
    }

    getConstants(
        tenantId: string,
        isContraAccount: boolean,
        isNextBudget: boolean = false,
        isIncomeAccount: boolean = false,
        isSalaryAccount: boolean = false
    ): Observable<BudgetConstant[]> {
        return this.http.get<BudgetConstant[]>(
            `${this.baseUrl}/constants/${tenantId}?isContraAccount=${isContraAccount}&isNextBudget=${isNextBudget}&isIncomeAccount=${isIncomeAccount}&isSalaryAccount=${isSalaryAccount}`
        );
    }

    deleteBudget(budget: Budget): Observable<HttpResponse<any>> {
        return this.http.delete(`${this.baseUrl}/${budget.id}`, { observe: 'response' });
    }

    createBudget(budget: Budget): Observable<HttpResponse<Budget>> {
        return this.http.post<Budget>(this.baseUrl, budget, { observe: 'response' });
    }

    updateBudget(budget: IBudget): Observable<HttpResponse<Budget>> {
        return this.http.put<Budget>(this.baseUrl, budget, { observe: 'response' });
    }

    approve(id: string): Observable<HttpResponse<Budget>> {
        return this.http.patch<Budget>(`${this.baseUrl}/approve/${id}`, null, { observe: 'response' });
    }

    searchBudgets(search: BudgetSearch): Observable<HttpResponse<IBudgetSearchResultDTO[]>> {
        return this.http.post<IBudgetSearchResultDTO[]>(`${this.baseUrl}/search`, search, { observe: 'response' });
    }

    importBudget(budgetImportDTO: BudgetImportDTO): Observable<HttpResponse<any>> {
        return this.http.post<any>(`${this.baseUrl}/import`, budgetImportDTO, { observe: 'response' });
    }

    downloadTemplate(
        schoolId: string,
        category: AccountCategory,
        sourceId: string,
        budgetYear: number
    ): Observable<HttpResponse<any>> {
        return this.http.get(
            `${this.baseUrl}/school/${schoolId}/category/${category}/budgetYear/${budgetYear}/download?sourceId=${sourceId}`,
            {
                observe: 'response',
                responseType: 'blob',
            }
        );
    }

    uploadExcelFile(
        budgetId: string,
        category: AccountCategory,
        sourceId: string,
        monthStart: number,
        monthEnd: number,
        formData: FormData
    ) {
        return this.http.post<any>(
            `${this.baseUrl}/${budgetId}/category/${category}/monthStart/${monthStart}/monthEnd/${monthEnd}/upload?sourceId=${sourceId}`,
            formData,
            {
                reportProgress: true,
                observe: 'events',
            }
        );
    }

    getActiveBySchoolId(id: string): Observable<HttpResponse<KeyValueDTO[]>> {
        return this.http.get<KeyValueDTO[]>(`${this.baseUrl}/active/school/${id}`, { observe: 'response' });
    }

    getBudgetKeyValueBySchoolId(id: string): Observable<HttpResponse<KeyValueDTO[]>> {
        return this.http.get<KeyValueDTO[]>(`${this.baseUrl}/school/${id}`, { observe: 'response' });
    }

    getBudgetKeyValueByBudgetGroupId(id: string): Observable<HttpResponse<KeyValueDTO[]>> {
        return this.http.get<KeyValueDTO[]>(`${this.baseUrl}/budget-group/${id}`, { observe: 'response' });
    }

    getBudgetKeyValueByBudgetGroupIdAndSchoolId(
        budgetGroupId: string,
        schoolId: string
    ): Observable<HttpResponse<KeyValueDTO[]>> {
        return this.http.get<KeyValueDTO[]>(`${this.baseUrl}/budget-group/${budgetGroupId}/school/${schoolId}`, {
            observe: 'response',
        });
    }

    unapprove(id: string): Observable<HttpResponse<Budget>> {
        return this.http.patch<Budget>(`${this.baseUrl}/unapprove/${id}`, null, { observe: 'response' });
    }

    setActive(id: string, isActive: boolean): Observable<HttpResponse<any>> {
        return this.http.patch<any>(`${this.baseUrl}/${id}/active/${isActive}`, {
            observe: 'response',
        });
    }

    setProgress(id: string, isProgress: boolean): Observable<HttpResponse<any>> {
        return this.http.patch<any>(`${this.baseUrl}/${id}/progress/${isProgress}`, {
            observe: 'response',
        });
    }

    getPermissionsByBudgetIdAndCategory(
        budgetId: string,
        category: AccountCategory
    ): Observable<HttpResponse<BudgetPermission[]>> {
        return this.http.get<BudgetPermission[]>(`${this.baseUrl}/${budgetId}/permissions/${category}`, {
            observe: 'response',
        });
    }

    copy(budgetCopy: BudgetCopy): Observable<HttpResponse<any[]>> {
        const url =
            budgetCopy.accountCategory.toString() == 'all' ? 's' : `-${budgetCopy.accountCategory.toLowerCase()}`;
        return this.http.post<any[]>(`/school-service/api/budget${url}/copy`, budgetCopy, { observe: 'response' });
    }
    ngOnDestroy() {
        if (this.importDataSubscriber) {
            this.importDataSubscriber.unsubscribe();
        }
    }
}
